import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  ColDef,
  GetRowIdFunc,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { filter, Observable, Subscription, tap } from 'rxjs';

@Directive()
export abstract class AgToolbarGridLifecycle<TFilterModel = unknown> implements OnInit, OnDestroy {
  @Input() noRowsOverlayMsg: string = 'No matching rows found';
  @Input() columnDefs!: ColDef[];
  @Input() pageSize: number = 10;
  @Input() gridOptions!: GridOptions;
  @Input() dataProvider?: () => IServerSideDatasource;
  @Input() rowData$?: Observable<unknown>;
  @Input() showToolbar: boolean = true;
  @Input() getRowId?: GetRowIdFunc;
  @Input() defaultFilter?: TFilterModel;
  @Input() refreshOn$?: Observable<boolean | undefined>;

  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() clearAllSortFilters = new EventEmitter<void>();
  @Output() paginationChanged = new EventEmitter<PaginationChangedEvent>();

  abstract readonly DEFAULT_GRID_OPTIONS: GridOptions;

  gridApi!: GridApi;

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.gridOptions = {
      ...this.DEFAULT_GRID_OPTIONS,
      ...this.createApplicationGridOptions(),
      ...this.gridOptions,
    };
    this.configureRowModel();

    const refreshSubscription = this.refreshOn$
      ?.pipe(
        filter((refresh) => this.shouldRefreshGrid(refresh)),
        tap(() => this.refreshGrid()),
      )
      .subscribe();

    if (refreshSubscription) {
      this.subscriptions.add(refreshSubscription);
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.prepareGridReady(event);
    this.gridReady.emit(event);

    const rowDataSubscription = this.rowData$?.subscribe((data) => {
      if (!event.api.isDestroyed()) {
        this.applyRowData(event, data);
      }
    });

    if (rowDataSubscription) {
      this.subscriptions.add(rowDataSubscription);
    }
  }

  onClearAllSortFilters(): void {
    this.gridApi?.setFilterModel({});
    this.gridApi?.resetColumnState();
    this.gridApi?.paginationGoToFirstPage();
    this.clearAllSortFilters.emit();
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    this.paginationChanged.emit(event);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected abstract createApplicationGridOptions(): GridOptions;
  protected abstract configureRowModel(): void;
  protected abstract shouldRefreshGrid(refresh: boolean | undefined): boolean;
  protected abstract refreshGrid(): void;
  protected abstract prepareGridReady(event: GridReadyEvent): void;
  protected abstract applyRowData(event: GridReadyEvent, data: unknown): void;
}
